// @file Composable that enables auto scrolling while dragging an item.
import { extractCustomMediaType, extractItemData, type ItemData } from '@@/pragmatic-drag-and-drop/helpers'
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element'
import { autoScrollForExternal } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/external'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types'
import { onBeforeUnmount, onMounted, type Ref } from 'vue'

/**
 * Enables auto scrolling for the scroll container in a draggable list.
 */
export const useAutoScrollElement = ({
  scrollContainerElementRef,
  type,
  allowedAxis,
}: {
  /**
   * Element to enable auto scrolling for.
   */
  scrollContainerElementRef: Ref<HTMLElement | undefined | null>
  /**
   * Auto scroll when dragging an item of this type or these types.
   */
  type: ItemData['type'] | Array<ItemData['type']>
  /**
   * Used to enable auto scrolling only on a particular axis.
   * Matches `@atlaskit/pragmatic-drag-and-drop-auto-scroll`'s internal type `AllowedAxis`.
   */
  allowedAxis?: 'horizontal' | 'vertical' | 'all'
}): void => {
  const enableAutoScroll = (): CleanupFn => {
    if (scrollContainerElementRef.value == null) return () => {}
    const getAllowedAxis = allowedAxis === undefined ? undefined : () => allowedAxis
    return combine(
      autoScrollForElements({
        element: scrollContainerElementRef.value,
        canScroll: ({ source }) => {
          // Handle both single type and array of types
          const sourceType = extractItemData(source)?.type
          if (typeof sourceType !== 'string' || sourceType === '') return false
          return Array.isArray(type) ? type.includes(sourceType) : sourceType === type
        },
        getAllowedAxis,
      }),
      autoScrollForExternal({
        element: scrollContainerElementRef.value,
        canScroll: ({ source }) => {
          // Handle both single type and array of types
          const sourceType = extractCustomMediaType(source)
          if (typeof sourceType !== 'string' || sourceType === '') return false
          return Array.isArray(type) ? type.includes(sourceType) : sourceType === type
        },
        getAllowedAxis,
      }),
    )
  }

  let cleanUp: CleanupFn

  onMounted(() => {
    cleanUp = enableAutoScroll()
  })

  onBeforeUnmount(() => {
    cleanUp?.()
  })
}
